<script setup>
// assets/project/src/components/Components/CustomFields.vue
import { ref, defineProps, defineEmits, toRefs, watch, defineExpose } from 'vue'
import 'ant-design-vue/dist/antd.css'
import TinymceEditor from '../Base/TinymceEditor3.vue'
import dayjs from 'dayjs'
import { DatePicker, Radio, Checkbox, message, Input } from "ant-design-vue"
import BasePdfUploader from '../Base/BasePdfUploader.vue'

const props = defineProps({
	project: {
		type: Object,
		required: true
	},
	competition: {
		type: Object,
		required: false
	},
	isDataUpdating: {
		type: Boolean,
		default: false
	},
	fields: {
		type: Array,
		default: () => []
	}
})

const { project, isDataUpdating, fields } = toRefs(props)

const emit = defineEmits(['update:project', 'save', 'validation'])

// Структура полей формы
const formFields = [
	{
		label: 'Фамилия',
		type: 'text',
		required: true,
		key: 'lastName',
	},
	{
		label: 'Имя',
		type: 'text',
		required: true,
		key: 'firstName',
	},
	{
		label: 'Отчество',
		type: 'text',
		required: false,
		key: 'middleName',
	},
	{
		label: 'Дата рождения',
		type: 'date',
		required: true,
		key: 'birthDate',
		format: 'DD.MM.YYYY'
	},
	{
		label: 'Готовы ли вы во время обучения присутствовать на всех занятиях, в том числе в будние дни и выходные?',
		type: 'radio',
		required: true,
		key: 'readyToAttend',
		options: [
			{ label: 'Да', value: 'yes' },
			{ label: 'Нет', value: 'no' }
		]
	},
	{
		label: 'Пропишите вашу фильмографию (если есть)',
		type: 'tinymce',
		required: false,
		key: 'filmography'
	},
	{
		label: 'Даю свое согласие на обработку персональных данных',
		type: 'checkbox',
		required: true,
		key: 'personalDataAgreement'
	}
]

// Данные формы
const formData = ref({})

// При изменении project обновляем formData
watch(() => props.project?.project_data?.custom_fields, (newVal) => {
	if (newVal) {
		formData.value = {...newVal}
	}
}, {immediate: true})

// Обработчик изменения даты
function handleDateChange(date, dateString, field) {
	formData.value[field.key] = dateString
	handleFieldChange()
}

// Обработчик изменений полей
function handleFieldChange() {
	const customFieldsData = { ...formData.value };
	
	// Подготовка данных перед отправкой родительскому компоненту
	fields.value.forEach(field => {
		if (field.type === 'radio') {
			const value = customFieldsData[field.key];
			if (value) {
				const selectedOption = field.options.find(opt => opt.value === value);
				if (selectedOption?.userInput && customFieldsData[field.key + '_custom']) {
					// Если это опция с пользовательским вводом и ввод заполнен,
					// добавляем специальный маркер в данные
					customFieldsData[field.key + '_is_custom'] = true;
				}
			}
		}
	});
	
	// Обновляем project_data через emit
	emit('update:project', {
		...props.project,
		project_data: {
			...props.project.project_data,
			custom_fields: customFieldsData
		}
	});
	
	// Вызываем метод сохранения в родительском компоненте
	emit('save');
}

function handleRadioChange(value, field) {
	// Проверяем, есть ли выбранная опция среди доступных
	const selectedOption = field.options.find(option => option.value === value);
	
	// Устанавливаем значение
	formData.value[field.key] = value;
	
	// Если это не опция с пользовательским вводом, очищаем поле пользовательского ввода
	if (!selectedOption?.userInput) {
		formData.value[field.key + '_custom'] = '';
	}
	
	handleFieldChange();
}

function handleCheckboxChange(e, field) {
	formData.value[field.key] = e.target.checked
	handleFieldChange()
}

function handleFileChange(field) {
	// Получаем актуальные данные проекта
	fetch(`/api/projects/${project.value.id}.json`)
		.then(response => response.json())
		.then(data => {
			if (data && data.project_data && data.project_data.custom_fields) {
				// Обновляем наши локальные данные без вызова save
				Object.assign(formData.value, data.project_data.custom_fields);
				
				// Обновляем также project.value.project_data.custom_fields
				// чтобы последующие PATCH запросы содержали актуальные данные
				if (!project.value.project_data) {
					project.value.project_data = {};
				}
				if (!project.value.project_data.custom_fields) {
					project.value.project_data.custom_fields = {};
				}
				
				Object.assign(project.value.project_data.custom_fields, data.project_data.custom_fields);
				
				// Не вызываем emit('save'), чтобы избежать лишнего PATCH запроса
			}
		})
		.catch(error => {
			console.error('Ошибка при получении данных проекта:', error);
		});
}

function handleTextareaInput(event, field) {
	// Обновляем значение в formData
	formData.value[field.key + '_custom'] = event.target.value;
	
	// Вызываем общий обработчик изменений
	handleFieldChange();
}

function handleFileDelete(field) {
	// Очищаем значение в formData
	formData.value[field.key] = '';
	
	// Если есть custom_fields в project_data, очищаем значение и там
	if (project.value.project_data && project.value.project_data.custom_fields) {
		project.value.project_data.custom_fields[field.key] = '';
	}
	
	// Обновляем интерфейс
	handleFieldChange();
}

const errors = ref([])
function validateFields() {
	errors.value = [];
	let isValid = true;
	
	fields.value.forEach(field => {
		if (field.required) {
			let fieldValue = formData.value[field.key];
			let isFieldValid = true;
			
			switch (field.type) {
				case 'text':
				case 'textarea':
				case 'tinymce':
					isFieldValid = fieldValue && fieldValue.trim().length > 0;
					break;
				case 'date':
					isFieldValid = fieldValue && fieldValue.length > 0;
					break;
				case 'radio':
					isFieldValid = fieldValue !== undefined && fieldValue !== null;
					
					// Дополнительная проверка для пользовательского ввода
					if (isFieldValid && field.options) {
						const selectedOption = field.options.find(opt => opt.value === fieldValue);
						if (selectedOption?.userInput) {
							// Если это опция с пользовательским вводом, проверяем заполнение поля ввода
							isFieldValid = formData.value[field.key + '_custom'] &&
								formData.value[field.key + '_custom'].trim().length > 0;
						}
					}
					break;
				case 'checkbox':
					isFieldValid = fieldValue === true;
					break;
				case 'file':
					isFieldValid = fieldValue && fieldValue.length > 0;
					break;
				default:
					isFieldValid = true;
			}
			
			if (!isFieldValid) {
				let message = `Поле "${field.label}" обязательно для заполнения`;
				
				// Специальное сообщение для радио с пользовательским вводом
				if (field.type === 'radio' && fieldValue) {
					const selectedOption = field.options.find(opt => opt.value === fieldValue);
					if (selectedOption?.userInput) {
						message = `Необходимо указать значение для опции "${selectedOption.label}"`;
					}
				}
				
				errors.value.push({
					field: field.key,
					message: message
				});
				isValid = false;
			}
		}
	});
	
	// Отправляем результат валидации в родительский компонент
	emit('validation', {
		isValid,
		errors: errors.value
	});
	
	return isValid;
}


defineExpose({
	validateFields,
	errors
})
</script>

<template>
	<div class="custom-fields">
		<div v-for="field in fields" :key="field.key" class="field">
			<label v-if="field.type !== 'checkbox'" :class="field.required ? 'req' : ''">{{ field.label }}</label>
			
			<input
				v-if="field.type === 'text'"
				type="text"
				v-model="formData[field.key]"
				:disabled="isDataUpdating"
				@change="handleFieldChange"
				:required="field.required"
			>
			
			<template v-if="field.type === 'tinymce'">
				<TinymceEditor
					:id="field.key"
					:label="field.label"
					:is-required="field.required"
					:is-data-updating="isDataUpdating"
					v-model="formData[field.key]"
					@onChange="handleFieldChange"
				/>
			</template>
			
			<textarea
				v-else-if="field.type === 'textarea'"
				v-model="formData[field.key]"
				:disabled="isDataUpdating"
				@change="handleFieldChange"
				:required="field.required"
			></textarea>
			
			<template v-if="field.type === 'checkbox'" >
				<div class="checkbox-field">
					<Checkbox
						:checked="formData[field.key]"
						:disabled="isDataUpdating"
						@change="e => handleCheckboxChange(e, field)"
						class="custom-checkbox"
					>
						<span :class="{ 'required-label': field.required }">{{ field.label }}</span>
					</Checkbox>
				</div>
			</template>
			
			<template v-if="field.type === 'date'">
				<DatePicker
					:value="formData[field.key] ? dayjs(formData[field.key], field.format) : null"
					:format="field.format"
					:disabled="isDataUpdating"
					:placeholder="'Выберите ' + field.label.toLowerCase()"
					class="date-picker"
					@change="(date, dateString) => handleDateChange(date, dateString, field)"
				/>
			</template>
			
			<template v-else-if="field.type === 'radio'">
				<Radio.Group
					:value="formData[field.key]"
					:disabled="isDataUpdating"
					@change="e => handleRadioChange(e.target.value, field)"
					class="radio-group"
				>
					<div v-for="option in field.options" :key="option.value" class="radio-option">
						<Radio
							:value="option.value"
							:disabled="isDataUpdating"
						>
							{{ option.label }}
						</Radio>
						
						<Input.TextArea
							v-if="option.userInput && formData[field.key] === option.value"
							v-model:value="formData[field.key + '_custom']"
							placeholder="Введите своё значение"
							class="custom-radio-textarea"
							:rows="4"
							:disabled="isDataUpdating"
							@blur="handleFieldChange"
						/>
					</div>
				</Radio.Group>
			</template>
			
			<template v-else-if="field.type === 'info'">
				<div class="info-field" v-html="field.info"></div>
			</template>
			
			<template v-else-if="field.type === 'file'">
				<BasePdfUploader
					:project="project"
					:currentProjectId="project.id"
					:value="formData[field.key]"
					pathToUpload="/project/uploadCustomFieldFile/"
					:pathToDelete="'/project/removeCustomFieldFile/'+project.id+'/'+field.key"
					:objectKey="field.key"
					:is-new-link="true"
					isTransKey="true"
					:is-required="field.required"
					@onChange="() => handleFileChange(field)"
					@onDelete="() => handleFileDelete(field)"
				/>
			</template>
			
		</div>
	</div>
</template>

<style scoped>
.custom-fields {
	margin: 20px 0;
}

.field {
	margin-bottom: 15px;
}

label {
	display: block;
	margin-bottom: 5px;
}

label.req:after, .required-label:after {
	content: '*';
	color: red;
	margin-left: 4px;
}

input, textarea {
	width: 100%;
	padding: 8px;
	border: 1px solid #ddd;
	border-radius: 4px;
}

textarea {
	min-height: 100px;
	resize: vertical;
}

input:disabled,
textarea:disabled {
	background-color: #f5f5f5;
	cursor: not-allowed;
}

.date-picker {
	width: 100%;
}

:deep(.ant-picker-input > input) {
	font-size: 14px;
}

.radio-group {
	display: flex;
	gap: 20px;
	margin-top: 5px;
}

.checkbox-field {
	margin-top: 10px;
}

.custom-checkbox {
	font-size: 17px;
	display: flex;
}

/* Стили для звездочки обязательного поля в чекбоксе */
.checkbox-field:deep(.ant-checkbox-wrapper) {
	align-items: center;
}
.checkbox-field:deep(.ant-checkbox + span) {
	padding-left: 15px;
}
.checkbox-field:deep(.ant-checkbox) {
	top: 1px;
	position: relative;
}

.info-field {
	padding: 15px;
	background-color: #f5f5f5;
	border-radius: 4px;
	margin: 10px 0;
}

.info-field :deep(p) {
	margin-bottom: 1em;
}

.info-field :deep(ul),
.info-field :deep(ol) {
	padding-left: 20px;
	margin-bottom: 1em;
}

.radio-option {
	margin-bottom: 8px;
	display: flex;
	flex-direction: column;
}

.custom-radio-textarea {
	margin-top: 5px;
	margin-left: 22px; /* Отступ, чтобы поле было на уровне с текстом радиокнопки */
	width: calc(100% - 22px);
	min-height: 80px; /* Добавляем минимальную высоту для textarea */
	resize: vertical; /* Позволяем вертикальное изменение размера */
}

/* Изменить существующий стиль .radio-group на: */
.radio-group {
	display: flex;
	flex-direction: column;
	gap: 5px;
	margin-top: 5px;
}
</style>